import itiriri from "itiriri";
import { parseInt } from "lodash-es";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { WardDto } from "server-openapi";
import styled from "styled-components";
import { IconBack } from "../../../kit/Icons/Back";
import { Layout } from "../../../kit/Layout";
import { Text } from "../../../kit/Text";
import { useSyncCenter } from "../../../syncstream/SyncCenterProvider";
import { useStore } from "../../../core/storage/hooks/UseStore";
import { DashboardNavbar } from "../../Dashboard/DashboardPage/components/DashboardNavbar";
import { RequirePermission } from "../../../components/RequirePermission/RequirePermission";
import { useGroupPermissions } from "../../../core/authz/PermissionsProvider";
import { FacilityMedicationChangesReportFilters } from "./components/FacilityMedicationChangesReportFilters";
import { FacilityMedicationChangesReportTable } from "./components/FacilityMedicationChangesReportTable";
import { useAsync } from "../../../kit/hooks/UseAsync";
import { apis } from "../../../core/mrs/apis";
import { Button } from "../../../kit/Button";
import { format } from "date-fns";

interface IParams {
  facilityGroupId: string;
}

// eslint-disable-next-line max-lines-per-function
function FacilityMedicationChangeReportContent() {
    const { facilityGroupId } = useParams<IParams>();
    const services = useSyncCenter();
    const facilityGroupsStore = useStore(services.facilityGroups.store).store;

    const facilityGroup = itiriri(facilityGroupsStore.values()).find(
        (facilityGroup) => facilityGroup.hsId?.toString() === facilityGroupId
    );

    // Group related state
    const [reportParams, setReportParams] = React.useState({
        facility: undefined as WardDto | undefined,
        selectedMonth: undefined as Date | undefined,
        searchedMonth: undefined as Date | undefined,
        isSearchActive: false
    });

    const handleMonthSelection = (date: Date | undefined) => {
        setReportParams(prev => ({ ...prev, selectedMonth: date }));
    };

    const handleSearch = () => {
        setReportParams(prev => ({
            ...prev,
            isSearchActive: true,
            searchedMonth: prev.selectedMonth
        }));
    };

    const { value: reportUrl, loading, error } = useAsync(async () => {
        if (!reportParams.isSearchActive || !reportParams.selectedMonth) {
            return null;
        }

        const startDateMonth = reportParams.selectedMonth.toDateString();

        // Step 1: Create the report job and get back the id/version immediately
        const startResponse = await apis.facilityMedicationChangesReports
            .facilityMedicationChangesReportStartFacilityMedicationChangesReport(
                parseInt(facilityGroupId),
                startDateMonth);

        const { id: reportId, version: initialVersion } = startResponse.data;

        // Step 2: Wait 2 seconds before first poll, then poll until finished
        await new Promise(resolve => setTimeout(resolve, 2000));

        let version: number | undefined = initialVersion ?? undefined;
        let finished = false;
        let status = '';

        for (let i = 0; i < 60; i++) {

            const statusResponse = await apis.facilityMedicationChangesReports
                .facilityMedicationChangesReportGetFacilityMedicationChangesReportStatus(
                    parseInt(facilityGroupId),
                    reportId ?? undefined,
                    version);

            finished = statusResponse.data.finished ?? false;
            status = statusResponse.data.status ?? '';
            version = statusResponse.data.version ?? undefined;

            if (finished)
                break;
        }

        if (!finished || status !== '2') {
            throw new Error(status === '4' ? 'Report generation failed' : 'Report generation timed out');
        }

        // Step 3: Fetch the file content - excel spreadsheet data in base64
        const contentResponse = await apis.facilityMedicationChangesReports
            .facilityMedicationChangesReportGetFacilityMedicationChangesReportContent(
                parseInt(facilityGroupId),
                reportId ?? undefined);

        if (!contentResponse.data.content) {
            return null;
        }

        const byteArray = new Uint8Array(
            window.atob(contentResponse.data.content)
                .split('')
                .map(char => char.charCodeAt(0))
        );

        // Create blob
        const blob = new Blob([byteArray], { type: 'application/vnd.ms-excel' });

        // Create blob url
        return URL.createObjectURL(blob);
    }, [facilityGroupId, reportParams.selectedMonth, reportParams.isSearchActive]);

    const handleExport = () => {
        if (reportUrl) {
            // Create a new link
            const anchor = document.createElement('a');
            anchor.href = reportUrl;

            const formattedDate = reportParams.selectedMonth
                ? format(reportParams.selectedMonth, 'MMM-yyyy').toLowerCase()
                : '';

            anchor.download = `medication_changes_report_${facilityGroupId}_${formattedDate}.xlsx`;

            // Append to the DOM
            document.body.appendChild(anchor);

            // Trigger `click` event
            anchor.click();

            // Remove element from DOM
            document.body.removeChild(anchor);
        }
    };

    return (
        <>
            <Layout horizontal style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Layout horizontal gap={1} style={{ alignItems: 'flex-start' }}>
                    <FacilityMedicationChangesReportFilters
                        selectedFacility={reportParams.facility}
                        onSelectFacility={(facility) =>
                            setReportParams(prev => ({ ...prev, facility }))}
                        facilityGroup={facilityGroup}
                        selectedStartMonth={reportParams.selectedMonth}
                        onSelectStartMonth={handleMonthSelection}
                    />
                    <Button
                        onClick={handleSearch}
                        disabled={!reportParams.selectedMonth}
                    >
                        Search
                    </Button>
                </Layout>
                <Button
                    disabled={!reportUrl || loading || !!error}
                    onClick={handleExport}
                >
                    Export to CSV
                </Button>
            </Layout>
            <FacilityMedicationChangesReportTable
                selectedStartMonth={reportParams.searchedMonth}
                shouldSearch={reportParams.isSearchActive}
            />
        </>
    );
}

export function FacilityMedicationChangesReportPage() {
    // Get the currently selected facility from the URL parameters.
    const groupPermissions = useGroupPermissions();
    const { facilityGroupId } = useParams<IParams>();
    const history = useHistory();
    const services = useSyncCenter();

    const facilityGroupsStore = useStore(services.facilityGroups.store).store;
    const facilityGroup = itiriri(facilityGroupsStore.values()).find(
        (facilityGroup) => facilityGroup.hsId?.toString() === facilityGroupId,
    );

    return (
        <Layout>
            <DashboardNavbar facilityGroup={facilityGroup} />
            <Container gap={1}>
                <Layout horizontal gap={1.5}>
                    <IconBack onClick={() => history.push(`/facility-group/${facilityGroupId}`)} />
                    <Text weight={'bold'} size="large" style={{ color: 'white' }}>
                        Facility Medication Changes
                    </Text>
                </Layout>
                <RequirePermission hasPermission={groupPermissions.canViewFacilityMedicationChangesReport}>
                    <FacilityMedicationChangeReportContent />
                </RequirePermission>
            </Container>
        </Layout>
    );
}

const Container = styled(Layout)`
  margin: 1.5rem;
`;
